import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import {
  adminCreateUser,
  adminDeleteUser,
  adminSendUserInviteEmail,
  adminUpdateUser,
  fetchAllSystemUsers,
} from "../api/user.api";
import { UserCreateBody, UserUpdateBody } from "../api/models/user.models";
import { useTranslation } from "react-i18next";

interface UseAllUsersProps {
  skip?: number;
  take?: number;
  name?: string;
  email?: string;
  companyId?: number;
  companyCode?: string;
  enabled?: boolean;
}

const useAllUsers = (options?: UseAllUsersProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [bearerToken, toast] = useUIStore(
    useShallow((state: UIState) => [state.bearerToken, state.toastRef]),
  );

  const {
    data: usersData,
    error: usersError,
    isLoading: usersIsLoading,
  } = useQuery({
    queryKey: [
      "allUsers",
      options?.skip,
      options?.take,
      options?.name,
      options?.email,
      options?.companyId,
      options?.companyCode,
    ],
    queryFn: () => fetchAllSystemUsers(bearerToken as string, options),
    enabled: !!bearerToken && !!options?.enabled,
  });

  // Create new company
  const createNewUser = async (company: UserCreateBody) => {
    const result = await createUserMutation.mutateAsync(company);
    return result;
  };

  const createUserMutation = useMutation({
    mutationFn: (payload: UserCreateBody) =>
      adminCreateUser(bearerToken as string, payload),
    onError: (error) => {
      console.error("Failed to create user:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("label-user-create-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("label-user-created"),
        life: 3000,
      });
    },
  });

  const updateUser = async (company: UserUpdateBody) => {
    const result = await adminUpdateUserMutation.mutateAsync(company);
    return result;
  };

  const adminUpdateUserMutation = useMutation({
    mutationFn: (payload: UserUpdateBody) =>
      adminUpdateUser(bearerToken as string, payload),
    onError: (error) => {
      console.error("Failed to update user:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("label-user-update-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("label-user-updated"),
        life: 3000,
      });
    },
  });

  // Send user invite email
  const sendUserInvite = async ({
    userId,
    companyId,
  }: {
    userId: number;
    companyId: number;
  }) => {
    const result = await adminSendUserInviteEmailMutation.mutateAsync({
      companyId,
      userId,
    });
    return result;
  };

  const deleteUser = async (userId: number) => {
    const result = await deleteUserMutation.mutateAsync(userId);
    return result;
  };

  const deleteUserMutation = useMutation({
    mutationFn: (userId: number) =>
      adminDeleteUser(bearerToken as string, userId),
    onError: (error) => {
      console.error("Failed to delete user:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("label-user-delete-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["allUsers"],
      });
      toast?.current?.show({
        severity: "success",
        detail: t("label-user-deleted"),
        life: 3000,
      });
    },
  });

  const adminSendUserInviteEmailMutation = useMutation({
    mutationFn: (payload: { companyId: number; userId: number }) =>
      adminSendUserInviteEmail(bearerToken as string, payload),
    onError: (error) => {
      console.error("Failed to send user invite email:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("label-user-invite-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("label-user-invite-sent"),
        life: 3000,
      });
      queryClient.invalidateQueries({
        queryKey: ["allUsers"],
      });
    },
  });

  const savingNewUser = createUserMutation.isPending;
  const sendingUserInvite = adminSendUserInviteEmailMutation.isPending;
  const updatingUser = adminUpdateUserMutation.isPending;
  const isDeletingUser = deleteUserMutation.isPending;

  return {
    users: usersData?.data,
    usersData,
    totalUsers: usersData?._count || 0,
    usersError,
    usersIsLoading,
    createNewUser,
    savingNewUser,
    sendUserInvite,
    sendingUserInvite,
    updateUser,
    updatingUser,
    deleteUser,
    isDeletingUser,
  };
};

export default useAllUsers;
